// Brew API client — fetch wrapper with bearer-token injection.
//
// Avoids axios to keep the dep tree small. Same-origin in dev (Vite proxies
// /api/* and /auth/* to the gateway). The bearer token is read fresh on
// every call so a refresh in another tab/context propagates immediately.

const TOKEN_KEY = 'brew.access_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private-mode browser, swallow */
  }
}

/** Session expired / token no longer valid. There is no client-side refresh
 *  flow, so clear the dead token and bounce to /login. Shared by the fetch
 *  wrapper and the streaming chat path (which bypasses `request`). Guarded on
 *  pathname to avoid a redirect loop when already on /login. */
export function handleSessionExpired(): void {
  setToken(null);
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

/** True for the connector sign-in endpoints, whose 401 means "the connector's
 *  upstream rejected these credentials" — not that the Brew session died. Those
 *  401s are shown inline by the sign-in modal and must NOT trigger a logout. */
function isConnectorLogin(path: string): boolean {
  return /\/api\/auth\/connectors\/[^/]+\/login(-direct|-init|-otp|-resend)$/.test(path);
}

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

function asApiError(status: number, body: unknown, message: string): ApiError {
  const err = new Error(message) as ApiError;
  err.status = status;
  err.body = body;
  return err;
}

/** Internal fetch wrapper. Adds the bearer if present, surfaces non-2xx
 *  as a structured ApiError so callers can read .status + .body. */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  let body: unknown = text;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      /* not JSON, keep as text */
    }
  }
  // A request that carried a bearer came back 401 → the session expired.
  // Guard on `token` so a failed login/register (no token yet) still surfaces
  // its 401 to the caller for error display instead of redirecting.
  //
  // Exception: the connector-login endpoints return 401 to mean "the
  // connector's UPSTREAM (Lumara / ESG) rejected these credentials" — the
  // Brew session is fine (these routes sit behind requireAuth, which already
  // passed). The sign-in modal surfaces that 401 inline as "Invalid email or
  // password"; treating it as Brew-session expiry would wipe the token and
  // bounce the user to /login just for mistyping their connector password.
  if (res.status === 401 && token && !isConnectorLogin(path)) {
    handleSessionExpired();
  }
  if (!res.ok) {
    const msg = (typeof body === 'object' && body && 'error' in (body as object))
      ? String((body as { error: unknown }).error)
      : `HTTP ${res.status}`;
    throw asApiError(res.status, body, msg);
  }
  return body as T;
}

export const api = {
  get:    <T>(path: string)              => request<T>(path),
  post:   <T>(path: string, body: unknown) => request<T>(path, { method: 'POST',  body: JSON.stringify(body) }),
  put:    <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT',   body: JSON.stringify(body) }),
  patch:  <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string)              => request<T>(path, { method: 'DELETE' }),
};
